/**
 * The device's side of the sync: the mirrored keys read and written through a store that may be
 * missing, blocked or full - none of which may ever throw into the game.
 */

import { keepBackup } from './backup';
import type { MergePlan } from './merge';
import type { CloudKey, CloudState, KeyValueStore, SaveDocument } from './types';
import { CLOUD_KEY, CLOUD_KEYS } from './types';

export interface LocalStore {
  read(key: string): string | null;
  /** False when the value could not be stored. */
  write(key: string, value: string): boolean;
  values(): Record<CloudKey, string | null>;
}

export function createLocalStore(storage: KeyValueStore | null): LocalStore {
  const read = (key: string): string | null => {
    try {
      return storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  };
  return {
    read,
    write(key, value) {
      try {
        storage?.setItem(key, value);
        return storage !== null;
      } catch {
        return false;
      }
    },
    values: () => ({
      [CLOUD_KEY.profile]: read(CLOUD_KEY.profile),
      [CLOUD_KEY.suspend]: read(CLOUD_KEY.suspend),
      [CLOUD_KEY.settings]: read(CLOUD_KEY.settings),
      [CLOUD_KEY.bindings]: read(CLOUD_KEY.bindings),
    }),
  };
}

/**
 * Carry out a merge in full: back up what lost, write what the server won, move the bookkeeping
 * on. Only ever at start-up - nothing has read the saves yet, so they may still be replaced.
 */
export function applyMerge(
  storage: KeyValueStore, local: LocalStore, state: CloudState, doc: SaveDocument, plan: MergePlan, at: number,
): void {
  if (plan.conflict !== null) {
    const side = plan.conflict.winner === 'local' ? 'server' : 'local';
    keepBackup(storage, state, { at, why: 'conflict', side, keys: plan.backup });
  }
  const failed = new Set<CloudKey>();
  for (const key of CLOUD_KEYS) {
    const value = plan.take[key];
    if (value !== undefined && !local.write(key, value)) failed.add(key);
  }
  for (const key of CLOUD_KEYS) {
    const agreed = plan.agreed[key];
    // A value that could not be stored is not agreed: its base stays, and the next start tries again.
    if (agreed !== undefined && !failed.has(key)) state.base[key] = agreed;
  }
  state.revision = doc.revision;
  state.dirty = plan.dirty;
  if (state.dirty.length === 0) state.dirtyAt = null;
  state.syncedAt = at;
}
